import { Wrapper as RawWrapper, Content } from 'ui/sidebar';
import Breadcrumb from 'components/Breadcrumb';
import {
  PageHeader,
  Container,
  media,
  size,
  Grid,
  Cell,
  Media,
  MediaImage,
  MediaBody,
  List,
} from 'ui';
import { serviceAnytimeTheme } from 'ui/colors';
import styled, { ThemeProvider } from 'styled-components';
import ascenceur from 'images/icons/ic-ascenseur.svg';
import brisDeGlace from 'images/icons/ic-bris-de-glace.svg';
import chauffage from 'images/icons/ic-chauffage.svg';
import electricite from 'images/icons/ic-electricite.svg';
import fuiteEau from 'images/icons/ic-fuite eau.svg';
import garage from 'images/icons/ic-garage.svg';
import incendie from 'images/icons/ic-incendie.svg';
import slash from 'images/icons/ic_slash.svg';
import serviceAnytimeSVG from 'images/icons/service-anytime.svg';
import serviceAnytimePNG from 'images/serviceanytime.png';
import { useTranslation, Trans } from 'react-i18next';
import { CardElement } from '@tymate/sergic';

const Wrapper = styled(RawWrapper)`
  color: ${({ theme }) => theme.text};
`;

const ServiceAnytimeTitleCard = styled.div`
  background-color: ${({ theme }) => theme.primary};
  text-align: center;
  color: #fff;
  font-weight: 700;
  text-align: center;
  font-size: ${size(2)};
  line-height: 1.4;
  margin-left: ${({ theme }) => theme.spacing(-1)};
  margin-right: ${({ theme }) => theme.spacing(-1)};
  width: calc(100% + ${({ theme }) => theme.spacing(2)});
  margin-bottom: ${({ theme }) => theme.spacing()};

  > ${Grid} {
    margin-top: ${({ theme }) => theme.spacing(-2)};
  }

  ${media.tablet`
    padding: ${({ theme }) => theme.spacing(1.5)};
    margin-left: 0;
    margin-right: 0;
    width: 100%;
    border-radius: ${({ theme }) => theme.borderRadius};

    > ${Grid} {
      margin-top: ${({ theme }) => theme.spacing(-1)};
    }
  `}

  span {
    display: block;
    color: ${({ theme }) => theme.primaryLight};
  }
`;

const ServiceAnytimeTitleCardContent = styled.div`
  padding-top: ${({ theme }) => theme.spacing()};
  padding-bottom: ${({ theme }) => theme.spacing()};

  > p {
    margin-bottom: 0;
  }
`;

const Equipment = styled(Media)`
  font-weight: 600;
  min-height: 64px;
  font-size: ${size(1)};

  p {
    margin-top: 0;
    margin-bottom: 0;
  }

  small {
    display: block;
    font-size: ${size()};
    font-weight: 400;
    margin-top: ${({ theme }) => theme.spacing(0.25)};
  }

  + li {
    margin-top: ${({ theme }) => theme.spacing()};
  }
`;

const ContactBoxHeader = styled(Media)`
  margin-bottom: ${({ theme }) => theme.spacing()};
`;

const ContactBoxHeaderTitle = styled.h2`
  margin: 0;
  font-size: ${size(1)};
  font-weight: 600;
`;

const ContactBox = styled.div`
  width: 100%;
  box-sizing: border-box;
  border: 8px solid currentColor;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing()};

  h2 {
    position: absolute;
    top: 0;
    left: 50%;
    text-transform: uppercase;
    width: 75%;
    background-color: #fff;
    text-align: center;
    transform: translate3d(-50%, -50%, 0);
    margin: 0;
    padding-left: ${({ theme }) => theme.spacing(0.5)};
    padding-right: ${({ theme }) => theme.spacing(0.5)};
    font-size: ${size(1)};

    ${media.tablet`
      font-size: ${size(2)};
    `}
  }

  ${media.desktop`
    padding: ${({ theme }) => theme.spacing(1.5)};
  `}
`;

const PhoneNumber = styled.a`
  font-size: ${size(3)};
  font-weight: 900;
  margin-top: ${({ theme }) => theme.spacing(3)};
  color: inherit;
  color: ${({ theme }) => theme.primaryLight};
  text-decoration: none;

  ${media.medium`
    font-size: ${size(4)};
  `}
`;

const PhoneNumberLegend = styled.span`
  font-size: ${({ theme }) => theme.spacing()};
  font-weight: bold;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.primaryLight};
`;

const ActionsList = styled.ul`
  list-style-image: url(${slash});
  list-style-position: outside;
  padding: 0;
  margin-left: ${({ theme }) => theme.spacing()};
  font-weight: 600;

  > li + li {
    margin-top: ${({ theme }) => theme.spacing(0.5)};
  }
`;

const MainImg = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  max-height: 180px;
  object-fit: cover;

  ${media.tablet`
    border-radius: ${({ theme }) => theme.borderRadius};
    max-height: 280px;
  `}
`;

const Logo = styled.img`
  background-color: #fff;
  padding: ${({ theme }) => theme.spacing(0.5)}
    ${({ theme }) => theme.spacing()};
`;

const Footer = styled.div`
  line-height: 1.5;
  padding: ${({ theme }) => theme.spacing()};
  background-color: #fff;
  width: 100%;
  text-align: left;

  p {
    opacity: 0.6;
    margin: 0;
  }

  ${media.desktop`
    line-height: ${({ theme }) => theme.spacing(1.25)};
    padding: ${({ theme }) => theme.spacing(0.75)};
    font-size: ${size(-1)};

    p {
      opacity: 1;
    }
  `}
`;

const equipments = [
  {
    img: fuiteEau,
    title: 'equipments.leak.title',
  },
  {
    img: electricite,
    title: 'equipments.electricity.title',
    description: 'equipments.electricity.description',
  },
  {
    img: chauffage,
    title: 'equipments.heating.title',
    description: 'equipments.heating.description',
  },
  {
    img: ascenceur,
    title: 'equipments.elevator.title',
  },
  {
    img: garage,
    title: 'equipments.parking.title',
  },
  {
    img: brisDeGlace,
    title: 'equipments.brokenGlass.title',
    description: 'equipments.brokenGlass.description',
  },
  {
    img: incendie,
    title: 'equipments.fire.title',
  },
];

const ServiceAnytime = () => {
  const { t } = useTranslation('serviceAnytime');

  return (
    <ThemeProvider theme={serviceAnytimeTheme}>
      <Wrapper>
        <Content>
          <PageHeader>
            <Breadcrumb />
          </PageHeader>

          <Container leftAligned medium>
            <ServiceAnytimeTitleCard>
              <Grid variant="singleLine" verticalAlign="center">
                <Cell sizes={{ default: 1, medium: 1 / 2 }}>
                  <MainImg src={serviceAnytimePNG} alt="" />
                </Cell>

                <Cell sizes={{ default: 1, medium: 1 / 2 }}>
                  <ServiceAnytimeTitleCardContent>
                    <Logo src={serviceAnytimeSVG} alt="" />

                    <div>
                      <Trans i18nKey="serviceAnytime:title">
                        Mon syndic disponible <span>24h/24 & 7j/7</span> en cas
                        d’urgence
                      </Trans>
                    </div>
                  </ServiceAnytimeTitleCardContent>
                </Cell>
              </Grid>
            </ServiceAnytimeTitleCard>

            <Grid verticalAlign="stretch">
              <Cell sizes={{ default: 1, medium: 1 / 2 }}>
                <CardElement isPadded hasNoBottomMargin>
                  <List style={{ width: '100%' }}>
                    {equipments.map(({ img, title, description }, index) => (
                      <Equipment as="li" key={index} center>
                        <MediaImage>
                          <img src={img} alt={title} />
                        </MediaImage>

                        <MediaBody>
                          <p>
                            {t(title)}
                            {Boolean(description) && (
                              <small>{t(description)}</small>
                            )}
                          </p>
                        </MediaBody>
                      </Equipment>
                    ))}
                  </List>
                </CardElement>
              </Cell>

              <Cell sizes={{ default: 1, medium: 1 / 2 }}>
                <CardElement isPadded hasNoBottomMargin>
                  <ContactBoxHeader center>
                    <MediaImage>
                      <img src={serviceAnytimeSVG} alt="" />
                    </MediaImage>

                    <MediaBody>
                      <ContactBoxHeaderTitle>
                        {t('contactBox.title')}
                      </ContactBoxHeaderTitle>
                    </MediaBody>
                  </ContactBoxHeader>

                  <ContactBox>
                    <h2>{t('contactBox.box.title')}</h2>
                    <PhoneNumber href={`tel:${t('phoneNumber')}`}>
                      {t('formattedPhoneNumber')}
                    </PhoneNumber>
                    <PhoneNumberLegend>{t('phoneLegend')}</PhoneNumberLegend>
                    <ActionsList>
                      <li>{t('contactBox.box.actions.first')}</li>
                      <li>{t('contactBox.box.actions.second')}</li>
                      <li>{t('contactBox.box.actions.third')}</li>
                    </ActionsList>
                  </ContactBox>
                </CardElement>
              </Cell>

              <Cell sizes={{ default: 1 }}>
                <Footer>
                  <p>{t('conditions')}</p>
                </Footer>
              </Cell>
            </Grid>
          </Container>
        </Content>
      </Wrapper>
    </ThemeProvider>
  );
};

export default ServiceAnytime;
